/**
 * §P.12 · El temario técnico del club.
 *
 * ## Lo que hace útil esta pantalla es el orden
 *
 * Un temario se estudia de abajo arriba —blanco, amarillo, naranja— y dentro de cada
 * cinturón en el orden en que se enseña. El servidor ya lo devuelve así, y la
 * pantalla no reordena nada: agrupa por cinturón y pinta. Una lista alfabética sería
 * correcta e inútil.
 *
 * ## El temario global y el propio salen juntos
 *
 * Para el alumno son lo mismo: lo que tiene que saberse. La diferencia solo importa
 * para saber qué se puede tocar, y eso lo dice `is_mine`. Separarlos en dos listas
 * obligaría al maestro a leer dos veces para saber qué entra en el examen.
 *
 * ## Kiap no aloja vídeo
 *
 * Se dice en el formulario, porque si no la primera pregunta es dónde se sube el
 * archivo. El club pega el enlace al vídeo que ya tiene publicado.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { Cinturon } from '@/club/grados/Cinturon';
import type {
  Cinturon as TipoDeCinturon,
  SistemaDeGrados,
} from '@/club/grados/tipos';
import {
  categoriasUsadas,
  enlaceValido,
  porCinturon,
  siguienteOrden,
  type TecnicaDelTemario,
} from '@/club/grados/tiposDelTemario';
import { enlaceSeguro } from '@/shared/ui/enlaceSeguro';

/** Lo que se está editando: una técnica existente, o una nueva. */
type EnEdicion =
  { modo: 'nueva' } | { modo: 'editar'; tecnica: TecnicaDelTemario } | null;

export function Temario({
  club,
  puedeEditar,
  puedeQuitar,
}: {
  club: string;
  puedeEditar: boolean;
  puedeQuitar: boolean;
}) {
  const cache = useQueryClient();
  const [soloMias, setSoloMias] = useState(false);
  const [categoria, setCategoria] = useState('');
  const [edicion, setEdicion] = useState<EnEdicion>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const temario = useQuery({
    queryKey: ['temario', club, soloMias, categoria],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).curriculum.get({
        query: {
          ...(soloMias ? { only_mine: true } : {}),
          ...(categoria === '' ? {} : { category: categoria }),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TecnicaDelTemario[];
    },
  });

  /*
   * Las categorías del filtro salen de **todo** el temario y no de lo filtrado: si
   * salieran de lo filtrado, elegir una dejaría el desplegable con esa sola opción y
   * no habría forma de volver ni de cambiar a otra.
   */
  const todo = useQuery({
    queryKey: ['temario', club, false, ''],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .curriculum.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TecnicaDelTemario[];
    },
  });

  const invalidar = () =>
    void cache.invalidateQueries({ queryKey: ['temario', club] });

  const quitar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.clubs({ club }).curriculum({ id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setConfirmando(null);
      invalidar();
    },
    onError: (e) => {
      setConfirmando(null);
      setProblema(aProblema(e));
    },
  });

  if (edicion) {
    return (
      <FormularioDeTecnica
        club={club}
        edicion={edicion}
        tecnicas={todo.data ?? []}
        alCerrar={() => setEdicion(null)}
        alGuardar={() => {
          setEdicion(null);
          invalidar();
        }}
      />
    );
  }

  if (temario.isPending) {
    return <Cargando filas={5} etiqueta="Cargando el temario" />;
  }

  if (temario.isError || !temario.data) {
    return (
      <PanelError
        problema={aProblema(temario.error)}
        reintentar={() => void temario.refetch()}
      />
    );
  }

  /*
   * **La categoría elegida sigue en la lista aunque ya no la use nadie.**
   *
   * Al quitar la última técnica de «pumsae», esa categoría desaparece de las usadas y
   * el `<option>` con ella se va del desplegable. Pero el estado sigue filtrando por
   * ella, así que el control se quedaba enseñando «Todas» mientras la pantalla decía
   * «nada con ese filtro»: el mando y lo que hace, contando cosas distintas.
   *
   * Se resuelve conservando la opción mientras el filtro la señale, no borrando el
   * filtro por detrás: quien acaba de quitar una técnica no ha pedido cambiar de
   * vista, y volver a «Todas» es un clic que decide él.
   */
  const usadas = categoriasUsadas(todo.data ?? temario.data);
  const categorias =
    categoria !== '' && !usadas.includes(categoria)
      ? [...usadas, categoria].sort((a, b) => a.localeCompare(b, 'es'))
      : usadas;
  const bloques = porCinturon(temario.data);
  const hayFiltro = soloMias || categoria !== '';

  return (
    <section>
      <div className="titulo-fila">
        <h1>Temario</h1>
        {puedeEditar && (
          <button type="button" onClick={() => setEdicion({ modo: 'nueva' })}>
            Añadir técnica
          </button>
        )}
      </div>

      <p className="tenue">
        Lo que hay que saberse para cada cinturón. Kiap no guarda vídeo: si lo
        tenéis publicado en vuestro canal o en la web de la federación, se pega
        el enlace y se abre desde aquí.
      </p>

      {problema && <PanelError problema={problema} />}

      {(categorias.length > 0 || soloMias) && (
        <div className="filtros">
          {categorias.length > 0 && (
            <label>
              Categoría
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
              >
                <option value="">Todas</option>
                {categorias.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Qué se ve
            <select
              value={soloMias ? 'mias' : 'todas'}
              onChange={(e) => setSoloMias(e.target.value === 'mias')}
            >
              <option value="todas">El temario entero</option>
              <option value="mias">Solo lo que ha puesto el club</option>
            </select>
          </label>
        </div>
      )}

      {bloques.length === 0 ? (
        hayFiltro ? (
          <Vacio
            titulo="Nada con ese filtro"
            explicacion="Prueba a quitarlo: puede que esa categoría solo esté en el temario global, o al revés."
          />
        ) : (
          <Vacio
            titulo="Todavía no hay temario"
            explicacion="Aquí se escribe lo que hay que saberse para cada cinturón: las técnicas, los pumsae, la teoría. Se puede empezar por el primer grado y seguir cuando haya rato."
            accion={
              puedeEditar ? (
                <button
                  type="button"
                  onClick={() => setEdicion({ modo: 'nueva' })}
                >
                  Añadir la primera
                </button>
              ) : undefined
            }
          />
        )
      ) : (
        bloques.map((bloque) => (
          <section key={bloque.belt_rank_id} className="bloque-de-temario">
            <h2>{bloque.rank_name}</h2>

            <ul className="papeles">
              {bloque.tecnicas.map((t) => (
                <li key={t.id}>
                  <div className="aviso-cabeza">
                    <strong>{t.title}</strong>
                    {t.category && (
                      <span className="pastilla pastilla-left">
                        {t.category}
                      </span>
                    )}
                    {!t.is_mine && (
                      /*
                       * «De la federación» y no «global»: el maestro no sabe qué
                       * es un temario global, pero sabe perfectamente qué es lo
                       * que manda su federación y que eso no lo toca él.
                       */
                      <span className="pastilla">De la federación</span>
                    )}
                  </div>

                  {t.description && <p>{t.description}</p>}

                  {t.external_url && (
                    <p>
                      {/*
                        `noreferrer` además de `noopener`: es una URL que ha
                        tecleado un club, y el sitio de destino no tiene por qué
                        saber desde qué panel se llega.
                      */}
                      <a
                        href={enlaceSeguro(t.external_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver el vídeo ↗
                      </a>
                    </p>
                  )}

                  {t.is_mine && (puedeEditar || puedeQuitar) && (
                    <div className="aviso-pie">
                      <span className="tenue">Orden: {t.sort_order}</span>
                      <div className="hueco-acciones">
                        {puedeEditar && (
                          <button
                            type="button"
                            className="btn-plano"
                            onClick={() =>
                              setEdicion({ modo: 'editar', tecnica: t })
                            }
                          >
                            Editar
                          </button>
                        )}
                        {puedeQuitar &&
                          (confirmando === t.id ? (
                            <>
                              <button
                                type="button"
                                className="btn-plano"
                                disabled={quitar.isPending}
                                onClick={() => quitar.mutate(t.id)}
                              >
                                Sí, quitarla
                              </button>
                              <button
                                type="button"
                                className="btn-plano"
                                onClick={() => setConfirmando(null)}
                              >
                                No
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn-plano"
                              onClick={() => {
                                setProblema(null);
                                setConfirmando(t.id);
                              }}
                            >
                              Quitar
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </section>
  );
}

/**
 * Añadir o corregir una técnica.
 *
 * ## El cinturón se elige en dos pasos, y no es burocracia
 *
 * No hay una ruta que devuelva los cinturones del club: van por sistema de grados, y
 * un club con taekwondo y hapkido tiene dos escaleras. «El cinturón amarillo» no
 * identifica nada si no se dice de cuál. Cuando el club solo sigue un sistema, el
 * primer desplegable no aparece: se elige solo.
 */
function FormularioDeTecnica({
  club,
  edicion,
  tecnicas,
  alCerrar,
  alGuardar,
}: {
  club: string;
  edicion: Exclude<EnEdicion, null>;
  tecnicas: readonly TecnicaDelTemario[];
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const existente = edicion.modo === 'editar' ? edicion.tecnica : null;

  const [sistemaId, setSistemaId] = useState('');
  const [cinturonId, setCinturonId] = useState(existente?.belt_rank_id ?? '');
  const [titulo, setTitulo] = useState(existente?.title ?? '');
  const [descripcion, setDescripcion] = useState(existente?.description ?? '');
  const [enlace, setEnlace] = useState(existente?.external_url ?? '');
  const [categoria, setCategoria] = useState(existente?.category ?? '');
  const [orden, setOrden] = useState(
    existente ? String(existente.sort_order) : ''
  );
  const [problema, setProblema] = useState<Problema | null>(null);

  const sistemas = useQuery({
    queryKey: ['grados', club, 'con-plantillas'],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })[
        /*
         * **Con las plantillas**, y no es lo mismo que en «Sistemas de grados».
         *
         * Un club puede seguir la escalera global de su federación sin haberla
         * clonado: es lo que deja `PUT /clubs/{club}/disciplines` al dar de alta la
         * disciplina, y es un estado normal —el club que no toca los cinturones no
         * necesita una copia propia—. `rankAvailable` en el servidor lo acepta
         * explícitamente: vale un cinturón de un sistema global mientras el club
         * imparta esa disciplina.
         *
         * Sin `include_templates` el formulario decía «el club no tiene sistema de
         * grados en uso» a un club que sí lo tiene, y no había forma de escribir el
         * temario sin clonar una escalera que no se quería tocar.
         */
        'belt-schemes'
      ].get({ query: { include_templates: true } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SistemaDeGrados[];
    },
  });

  /*
   * Las que el club sigue hoy, sean suyas o de la federación. `in_use` lo calcula el
   * servidor mirando `academy_disciplines`, así que vale igual para las dos.
   *
   * Un sistema clonado y abandonado sigue existiendo —no se borra, porque sus
   * cinturones están en el historial de los alumnos— y ofrecerlo aquí sería invitar a
   * escribir el temario de una escalera que ya no se usa.
   */
  const enUso = (sistemas.data ?? []).filter((s) => s.in_use);
  const elegido = sistemaId || (enUso.length === 1 ? enUso[0]!.id : '');

  const cinturones = useQuery({
    queryKey: ['cinturones', club, elegido],
    enabled: elegido !== '',
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId: elegido })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TipoDeCinturon[];
    },
  });

  const guardar = useMutation({
    mutationFn: async () => {
      const cuerpo = {
        title: titulo.trim(),
        description: descripcion.trim() === '' ? null : descripcion.trim(),
        external_url: enlace.trim() === '' ? null : enlace.trim(),
        category: categoria.trim() === '' ? null : categoria.trim(),
        sort_order:
          orden.trim() === ''
            ? siguienteOrden(tecnicas, cinturonId)
            : Number(orden),
      };

      if (existente) {
        const r = await api.v1
          .clubs({ club })
          .curriculum({ id: existente.id })
          .patch({ ...cuerpo, belt_rank_id: cinturonId });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data;
      }

      const r = await api.v1
        .clubs({ club })
        .curriculum.post({ ...cuerpo, belt_rank_id: cinturonId });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: alGuardar,
    onError: (e) => setProblema(aProblema(e)),
  });

  const enlaceMal = !enlaceValido(enlace);
  const listo = titulo.trim() !== '' && cinturonId !== '' && !enlaceMal;

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate();
      }}
    >
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Temario
        </button>
      </div>

      <h1>{existente ? 'Corregir la técnica' : 'Añadir una técnica'}</h1>

      {sistemas.isPending ? (
        <Cargando filas={2} etiqueta="Cargando los cinturones" />
      ) : enUso.length === 0 ? (
        <Vacio
          titulo="El club no tiene sistema de grados en uso"
          explicacion="Un temario se organiza por cinturones, así que primero hay que elegir la escalera. Se hace en «Sistemas de grados»."
        />
      ) : (
        <>
          <div className="rejilla">
            {enUso.length > 1 && (
              <label>
                Sistema de grados
                <select
                  value={elegido}
                  onChange={(e) => {
                    setSistemaId(e.target.value);
                    // El cinturón elegido era de la otra escalera.
                    setCinturonId('');
                  }}
                >
                  <option value="">Elige uno</option>
                  {enUso.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.discipline_name} · {s.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              Cinturón
              <select
                value={cinturonId}
                disabled={cinturones.data === undefined}
                onChange={(e) => setCinturonId(e.target.value)}
              >
                <option value="">Elige uno</option>
                {(cinturones.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* El color del cinturón elegido, que es como se reconoce de un vistazo. */}
          {cinturones.data && cinturonId !== '' && (
            <p className="sede-mapa">
              {(() => {
                const c = cinturones.data.find((x) => x.id === cinturonId);
                return c ? (
                  <Cinturon
                    color={c.color_hex}
                    color2={c.color_hex_2}
                    nombre={c.name}
                    deDan={c.is_dan}
                  />
                ) : null;
              })()}
            </p>
          )}

          <div className="rejilla">
            <label>
              Cómo se llama
              <input
                required
                maxLength={200}
                placeholder="Ap chagi"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
              />
            </label>

            <label>
              Categoría
              <input
                maxLength={40}
                placeholder="Pateo, pumsae, teoría…"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
              />
            </label>
          </div>

          <label>
            En qué consiste
            <textarea
              rows={4}
              maxLength={4000}
              placeholder="Patada frontal. Se sube la rodilla, se extiende y se recoge antes de bajar."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </label>

          <label>
            Enlace al vídeo
            <input
              type="url"
              maxLength={500}
              placeholder="https://…"
              value={enlace}
              onChange={(e) => setEnlace(e.target.value)}
            />
          </label>

          <p className="tenue">
            {enlaceMal ? (
              <strong>
                El enlace tiene que empezar por «https://». No es purismo: un
                enlace «http://» dentro de esta página lo bloquea el navegador y
                no abriría.
              </strong>
            ) : (
              <>
                <strong>Kiap no guarda vídeo.</strong> Se pega el enlace al que
                ya tengáis publicado —vuestro canal, la web de la federación— y
                desde aquí se abre.
              </>
            )}
          </p>

          <label>
            Orden dentro del cinturón
            <input
              type="number"
              min={0}
              placeholder={
                cinturonId === ''
                  ? 'Se pone al final'
                  : String(siguienteOrden(tecnicas, cinturonId))
              }
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
            />
          </label>

          <p className="tenue">
            En blanco va al final de su cinturón, que es lo normal. El número
            solo hace falta para colar una técnica entre otras dos.
          </p>

          {problema && <PanelError problema={problema} />}

          <div className="acciones">
            <button type="submit" disabled={!listo || guardar.isPending}>
              {guardar.isPending
                ? 'Guardando…'
                : existente
                  ? 'Guardar los cambios'
                  : 'Añadir al temario'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={guardar.isPending}
              onClick={alCerrar}
            >
              Cancelar
            </button>
          </div>
        </>
      )}
    </form>
  );
}
